import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import LottieAnimation from '../LottieAnimation/LottieAnimation';
import { useLottieAnimation } from '../../hooks/useLottieAnimation';
import tcbg1Img from '@/assets/images/ai/tcbg1.png';
import tanhaoIcon from '@/assets/images/ai/tanhao.png';
import time1Icon from '@/assets/images/ai/time1.png';
import dpIcon from '@/assets/images/ai/dp.png';

// 导入Lottie动画文件
import firework1Animation from '@/assets/lotties/firework_1.json';
import firework2Animation from '@/assets/lotties/firework_2.json';
import openGiftAnimation from '@/assets/lotties/open_gift.json';
import reward1Animation from '@/assets/lotties/reward_1.json';
import snowAnimation from '@/assets/lotties/snow.json';
import caidaiAnimation from '@/assets/lotties/caidai.json';

export interface AlertDialogConfig {
  type: 'confirm' | 'timeout' | 'complete' | 'warning' | 'info';
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  showCancel?: boolean;
  autoConfirmSeconds?: number;
  autoConfirmText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  onAutoConfirm?: () => void;
  showLottie?: boolean;
  lottieAnimation?: string; // Lottie动画文件名，如 'firework_1.json'
}

// 类型到默认Lottie动画的映射
const TYPE_LOTTIE_MAP: Record<string, any> = {
  confirm: snowAnimation,
  timeout: snowAnimation,
  complete: firework1Animation,
  warning: openGiftAnimation,
  info: firework2Animation,
};

interface AlertDialogProps {
  visible: boolean;
  config: AlertDialogConfig;
  onClose: () => void;
}

const AlertDialog: React.FC<AlertDialogProps> = ({ visible, config, onClose }) => {
  const [countdown, setCountdown] = useState<number>(0);
  const [isCountingDown, setIsCountingDown] = useState(false);

  // 使用 ref 保存回调函数，避免在倒计时过程中丢失引用
  const callbacksRef = useRef({
    onAutoConfirm: config.onAutoConfirm,
    onConfirm: config.onConfirm,
    onClose: onClose
  });

  // 更新回调函数引用
  useEffect(() => {
    callbacksRef.current = {
      onAutoConfirm: config.onAutoConfirm,
      onConfirm: config.onConfirm,
      onClose: onClose
    };
  }, [config.onAutoConfirm, config.onConfirm, onClose]);

  const {
    isPlaying: isLottieAnimationPlaying,
    currentConfig: lottieConfig,
    playFirework,
    handleAnimationComplete,
    handleAnimationClose,
  } = useLottieAnimation();

  // 自动确定倒计时
  useEffect(() => {
    // 重置状态
    setIsCountingDown(false);
    setCountdown(0);

    if (!visible || !config.autoConfirmSeconds || config.autoConfirmSeconds <= 0) {
      return;
    }

    // 开始倒计时
    setCountdown(config.autoConfirmSeconds);
    setIsCountingDown(true);

    const timer = setInterval(() => {
       setCountdown((prev) => {
         if (prev <= 1) {
           clearInterval(timer);
           setIsCountingDown(false);
           // 自动确定
           if (callbacksRef.current.onAutoConfirm) {
             callbacksRef.current.onAutoConfirm();
           } else if (callbacksRef.current.onConfirm) {
             callbacksRef.current.onConfirm();
           }
           callbacksRef.current.onClose();
           return 0;
         }
         return prev - 1;
       });
     }, 1000);

    return () => {
      clearInterval(timer);
      setIsCountingDown(false);
    };
  }, [visible, config.autoConfirmSeconds]);

  // 获取当前应该使用的Lottie动画数据
  const getCurrentLottieAnimation = () => {
    if (config.lottieAnimation) {
      // 如果指定了具体的动画文件名，根据文件名返回对应的动画数据
      const animationMap: Record<string, any> = {
        'firework_1.json': firework1Animation,
        'firework_2.json': firework2Animation,
        'open_gift.json': openGiftAnimation,
        'reward_1.json': reward1Animation,
        'snow.json': snowAnimation,
        'caidai.json': caidaiAnimation,
      };
      return animationMap[config.lottieAnimation] || TYPE_LOTTIE_MAP[config.type];
    }
    // 否则使用类型默认的动画
    return TYPE_LOTTIE_MAP[config.type];
  };

  // 播放Lottie动画
  useEffect(() => {
    if (visible && config.showLottie) {
      // setTimeout(() => {
      //   playFirework({
      //     duration: 3000,
      //     onComplete: handleAnimationComplete,
      //     onClose: handleAnimationClose,
      //   });
      // }, 300);
    }
  }, [visible, config.showLottie, playFirework]);

  const handleConfirm = () => {
    if (config.onConfirm) {
      config.onConfirm();
    }
    onClose();
  };

  const handleCancel = () => {
    if (config.onCancel) {
      config.onCancel();
    }
    onClose();
  };

  // 根据类型获取图标
  const getTypeIcon = () => {
    switch (config.type) {
      case 'timeout':
        return time1Icon;
      case 'complete':
        return dpIcon;
      case 'warning':
      case 'confirm':
      case 'info':
      default:
        return tanhaoIcon;
    }
  };

  // 根据类型获取样式配置
  const getTypeStyles = () => {
    switch (config.type) {
      case 'confirm':
        return {
          bgColor: 'bg-white',
          titleColor: 'text-gray-800',
          messageColor: 'text-gray-600',
          confirmBg: 'bg-blue-500 hover:bg-blue-600',
          cancelBg: 'bg-gray-300 hover:bg-gray-400',
          iconColor: 'text-blue-500',
        };
      case 'timeout':
        return {
          bgColor: 'bg-white',
          titleColor: 'text-red-600',
          messageColor: 'text-gray-600',
          confirmBg: 'bg-red-500 hover:bg-red-600',
          cancelBg: 'bg-gray-300 hover:bg-gray-400',
          iconColor: 'text-red-500',
        };
      case 'complete':
        return {
          bgColor: 'bg-white',
          titleColor: 'text-green-600',
          messageColor: 'text-gray-600',
          confirmBg: 'bg-green-500 hover:bg-green-600',
          cancelBg: 'bg-gray-300 hover:bg-gray-400',
          iconColor: 'text-green-500',
        };
      case 'warning':
        return {
          bgColor: 'bg-white',
          titleColor: 'text-orange-600',
          messageColor: 'text-gray-600',
          confirmBg: 'bg-orange-500 hover:bg-orange-600',
          cancelBg: 'bg-gray-300 hover:bg-gray-400',
          iconColor: 'text-orange-500',
        };
      default:
        return {
          bgColor: 'bg-white',
          titleColor: 'text-gray-800',
          messageColor: 'text-gray-600',
          confirmBg: 'bg-blue-500 hover:bg-blue-600',
          cancelBg: 'bg-gray-300 hover:bg-gray-400',
          iconColor: 'text-blue-500',
        };
    }
  };

  const styles = getTypeStyles();

  return (
    <AnimatePresence>
      {visible && (
        <>
          {/* 蒙层 - 最底层 z-40 */}
          <motion.div
            className="fixed inset-0 z-40 bg-black bg-opacity-30 backdrop-blur-sm//"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={config.showCancel ? handleCancel : undefined}
          />

          {/* Lottie动画层 - 中间层 z-45 */}
          {config.showLottie && (
            <motion.div
              className="fixed inset-0 z-100 pointer-events-none"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <LottieAnimation
                animationData={getCurrentLottieAnimation()}
                loop={false}
                autoplay={true}
                speed={1}
                fullscreen={true}
                showCloseButton={false}
                onComplete={handleAnimationComplete}
                onClose={handleAnimationClose}
              />
            </motion.div>
          )}

          {/* 弹窗内容层 - 最上层 z-50 */}
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >

            {/* 弹窗内容 */}
            <motion.div
              style={{
                backgroundImage: `url(${tcbg1Img})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }}
              className={`relative mx-12 w-full max-w-md rounded-2xl p-6 pt-10 shadow-2xl pointer-events-auto bg-white`}
              initial={{ scale: 0.7, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.7, opacity: 0, y: 30 }}
              transition={{ 
                duration: 0.4, 
                type: 'spring', 
                damping: 15, 
                stiffness: 300,
                bounce: 0.4
              }}
            >
              <img 
                    src={getTypeIcon()} 
                    alt="icon" 
                    className="w-20 h-20 object-contain absolute -top-10 left-[50%] translate-x-[-50%]"
                  />
              {/* 标题 */}
              <div className="mb-4 text-center">
          
                <h3 className={`text-xl font-semibold ${styles.titleColor}`}>
                  ✨ {config.title} ✨
                </h3>
              </div>

              {/* 消息内容 */}
              <div className="mb-6 text-center">
                <p className={`text-base leading-relaxed ${styles.messageColor}`}>
                  {config.message}
                </p>
              </div>

              {/* 自动确定倒计时提示 */}
              {isCountingDown && config.autoConfirmText && (
                <div className="mb-4 text-center">
                  <p className="text-sm text-gray-500">
                    {config.autoConfirmText.replace('{seconds}', countdown.toString())}
                  </p>
                </div>
              )}

              {/* 按钮区域 */}
              <div className="flex gap-3">
                {config.showCancel && (
                  <motion.button
                    className={`flex-1 rounded-[30px] ${styles.cancelBg} px-4 py-3 bg-[#EBEDFF] text-[#A782FF] font-medium transition-colors`}
                    onClick={handleCancel}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {config.cancelText || '取消'}
                  </motion.button>
                )}
                <motion.button
                  className={`${config.showCancel ? 'flex-1' : 'w-full'} rounded-[30px] ${styles.confirmBg} px-4 py-3 bg-gradient-to-r from-[#A782FF] to-[#828EFF] text-white font-medium transition-colors`}
                  onClick={handleConfirm}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {config.confirmText || '确定'}
                  {isCountingDown && ` (${countdown})`}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AlertDialog;